import React, { useEffect, useState, useMemo } from "react";
import axiosInstance from "../api/axiosInstance";
import "../styles/BillGeneration.css";
import { FaSearch } from 'react-icons/fa';
import { motion } from "framer-motion";
import { showGlobalToast } from '../utils/toast';
import OrderWithBillGen from './OrderWithBillGen';

type Subject = { key: string; value: string };

type GroupedCard = {
    paramKey: "table" | "order";
    paramValue: string;
    headerName: string;
    subjects: Subject[];
};


const BillGeneration: React.FC = () => {
    const [viewBy, setViewBy] = useState<"table" | "order">("table");
    const [cards, setCards] = useState<GroupedCard[]>([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");

    // Bill view state
    const [showBillView, setShowBillView] = useState(false);
    const [selectedBillItem, setSelectedBillItem] = useState<{ type: "table" | "order"; id: string } | null>(null);

    const restaurantGuid = useMemo(
        () => localStorage.getItem("restaurantGuid"),
        []
    );

    useEffect(() => {
        const fetchGroupedData = async () => {
            if (!restaurantGuid) return;
            setLoading(true);
            try {
                const res = await axiosInstance.get(
                    `/Order/GetGrouped/${viewBy}/${restaurantGuid}`
                );
                setCards(res.data);
            } catch (err: any) {
                console.error("Error fetching grouped data:", err);
                showGlobalToast(err.response?.data?.message ?? 'Error fetching grouped data', 'error');
            } finally {
                setLoading(false);
            }
        };
        fetchGroupedData();
    }, [restaurantGuid, viewBy]);

    const generateBill = (type: "table" | "order", id: string) => {
        setSelectedBillItem({ type, id });
        setShowBillView(true);
    };

    const handleBillClosed = (bill?: any) => {
        setShowBillView(false);
        setSelectedBillItem(null);
        
        // If bill was paid, remove it from the cards list
        if (bill && bill.paymentStatus === "PAID") {
            setCards(prevCards => 
                prevCards.filter(card => card.paramValue !== bill.id)
            );
        }
    };

    const filteredCards = useMemo(() => {
        if (!search.trim()) return cards;
        const lower = search.toLowerCase();
        return cards.filter(
            (card) =>
                card.headerName.toLowerCase().includes(lower) ||
                card.subjects.some(
                    (s) =>
                        s.key.toLowerCase().includes(lower) ||
                        s.value.toLowerCase().includes(lower)
                )
        );
    }, [cards, search]);

    return (
        <div className="min-h-full bg-gray-100 dark:bg-gray-900 text-gray-900 dark:text-gray-100 p-4 sm:p-6">
            <div className="max-w-7xl mx-auto space-y-8">
                {/* Header and Controls */}
                <header className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
                    <h1 className="text-3xl font-extrabold text-gray-900 dark:text-white tracking-tight">
                        Bill Generation
                    </h1>
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto">
                        <div className="relative w-full sm:w-64">
                            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500" />
                            <input
                                type="text"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search..."
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-700 rounded-full bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-orange-500 transition-colors"
                            />
                        </div>
                        <div className="relative w-full sm:w-48">
                            <select
                                value={viewBy}
                                onChange={(e) => setViewBy(e.target.value as "table" | "order")}
                                className="block w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-full appearance-none bg-white dark:bg-gray-800 cursor-pointer focus:outline-none focus:ring-2 focus:ring-orange-500 transition-colors"
                            >
                                <option value="table">View By Table</option>
                                <option value="order">View By Order</option>
                            </select>
                            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700 dark:text-gray-300">
                                <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l-.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" /></svg>
                            </div>
                        </div>
                    </div>
                </header>

                <hr className="border-t border-gray-200 dark:border-gray-800" />

                {/* Cards Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {filteredCards.length > 0 ? (
                        filteredCards.map((card) => {
                            const status =
                                card.subjects.find(
                                    (s) =>
                                        s.key.toLowerCase() === "paid" || s.key.toLowerCase() === "status"
                                )?.value || "Payment Pending";

                            const isPaid = status.toLowerCase() === "paid";

                            return (
                                <motion.div
                                    key={`${card.paramKey}-${card.paramValue}`}
                                    initial={{ opacity: 0, y: 15 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: 15 }}
                                    transition={{ duration: 0.25 }}
                                    className="bg-white dark:bg-gray-900 rounded-2xl shadow-md hover:shadow-xl 
                     transition-transform duration-300 p-6 flex flex-col justify-between 
                     border border-gray-100 dark:border-gray-700"
                                >
                                    {/* Header */}
                                    <div className="flex justify-between items-start mb-4">
                                        <h3 className="font-bold text-lg text-gray-900 dark:text-gray-100">
                                            {card.headerName}
                                        </h3>
                                        <span
                                            className={`text-xs font-semibold uppercase px-3 py-1 rounded-full
              ${isPaid
                                                    ? "bg-green-100 text-green-700 dark:bg-green-800 dark:text-green-300"
                                                    : "bg-red-100 text-red-700 dark:bg-red-800 dark:text-red-300"
                                                }`}
                                        >
                                            {isPaid ? "Paid" : "Pending"}
                                        </span>
                                    </div>

                                    {/* Subjects */}
                                    <div className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
                                        {card.subjects.map((s, idx) => (
                                            <div
                                                key={idx}
                                                className="grid grid-cols-[auto,1fr] gap-4 border-b border-dashed border-gray-200 dark:border-gray-700 pb-1"
                                            >
                                                <span className="font-medium truncate">{s.key}</span>
                                                <span className="opacity-90 break-words text-right">{s.value}</span>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Button */}
                                    <motion.button
                                        whileTap={{ scale: 0.97 }}
                                        className="mt-6 w-full bg-orange-600 hover:bg-orange-700 
                       text-white py-2.5 px-4 rounded-xl font-semibold shadow-md 
                       transition-colors focus:outline-none focus:ring-2 
                       focus:ring-offset-2 focus:ring-orange-500 
                       dark:focus:ring-offset-gray-900"
                                        onClick={() => generateBill(card.paramKey, card.paramValue)}
                                    >
                                        Generate Bill
                                    </motion.button>
                                </motion.div>
                            );
                        })
                    ) : (
                        <div className="col-span-full text-center py-20">
                            <h2 className="text-2xl font-semibold text-gray-600 dark:text-gray-300">
                                No Bills Found
                            </h2>
                            <p className="mt-2 text-gray-500 dark:text-gray-400">
                                Try adjusting your search or "View By" filter.
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {/* Bill View */}
            {showBillView && selectedBillItem && (
                <OrderWithBillGen
                    tableId={selectedBillItem.id}
                    type={selectedBillItem.type === 'table' ? 'bill' : 'order'}
                    onBillClosed={handleBillClosed}
                />
            )}

            {/* Loading Overlay */}
            {loading && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] text-white text-base sm:text-lg transition-opacity duration-300 opacity-100">
                    <div className="flex flex-col items-center">
                        <svg className="animate-spin h-8 w-8 sm:h-10 sm:w-10 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <p className="mt-3 sm:mt-4">Loading...</p>
                    </div>
                </div>
            )}

        </div>
    );
};

export default BillGeneration;